import React, { useState } from "react";
import {
  X,
  Check,
  Eye,
  EyeOff,
  Mail,
  User as UserIcon,
  Lock,
  AlertCircle,
  Sparkles,
  Cpu,
  Palette,
  Rocket,
  ShieldCheck,
} from "lucide-react";
import { dbService } from "../services/dbService";
import { User, Language } from "../types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: User, isNewUser: boolean) => void;
  lang: Language;
}

const AuthModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, lang }) => {
  const [isLogin, setIsLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const validatePassword = (pass: string) => {
    const hasLetter = /[a-zA-Z]/.test(pass);
    const hasNumber = /[0-9]/.test(pass);
    const isLongEnough = pass.length >= 8;
    return hasLetter && hasNumber && isLongEnough;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isLogin) {
      if (!validatePassword(password)) {
        setError(
          "Password must contain at least 1 letter, 1 number, and be at least 8 characters long.",
        );
        return;
      }
      if (!agreeTerms) {
        setError("Please agree to the Terms and Conditions.");
        return;
      }
    }

    setIsLoading(true);

    try {
      if (isLogin) {
        const user = await dbService.login(email, password);
        onSuccess(user, false);
      } else {
        const user = await dbService.register({
          email,
          firstName,
          lastName,
          password,
        });
        onSuccess(user, true);
      }
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const t = {
    createAccount: "Create an account",
    login: "Log In",
    email: "Email address",
    firstName: "First name",
    lastName: "Last name",
    password: "Password",
    signUp: "Sign Up",
    signIn: "Log In",
    alreadyHave:
      lang === "zh"
        ? "已有账号？（支持RapidDirect账号登录）"
        : "Already have an account? (RapidDirect accounts supported)",
    dontHave: "Don't have an account?",
    agree: "I agree with the",
    terms: "Terms and Conditions",
    and: "and",
    privacy: "Privacy Policy",
    benefit1: "AI Concept Visualization",
    benefit2: "Manufacturing-Ready PRDs",
    benefit3: "Zero CAD Skills Required",
    benefit4: "Seamless Rapid Prototyping",
    heroTitle: "Chat Your Ideas into Reality",
    passwordHint1: "At least 1 letter",
    passwordHint2: "At least 1 number",
    passwordHint3: "At least 8 characters",
    regBonus:
      lang === "zh"
        ? "新注册立即赠送20点数"
        : "Get 20 free credits upon registration",
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6">
      <div className="absolute inset-0 bg-slate-950/60" onClick={onClose} />

      <div className="relative w-full max-w-5xl bg-white rounded-3xl shadow-xl overflow-hidden flex flex-col md:flex-row animate-fade-up border border-white/20 max-h-[95vh] md:max-h-[85vh] overflow-y-auto custom-scrollbar">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-10 p-2 hover:bg-slate-100 rounded-full transition-all hover:scale-110 active:scale-90"
        >
          <X className="w-6 h-6 text-slate-400" />
        </button>

        {/* Left Side - Marketing Hero */}
        <div className="md:w-5/12 bg-slate-950 p-8 md:p-10 flex flex-col justify-between relative overflow-hidden group">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -left-20 w-80 h-80 bg-red-600/20 blur-[100px] rounded-full"></div>
            <div
              className="absolute -bottom-20 -right-20 w-80 h-80 bg-blue-600/20 blur-[100px] rounded-full"
              style={{ animationDelay: "2s" }}
            ></div>
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
          </div>

          <div className="relative z-10">
            <div className="w-10 h-10 flex items-center justify-center mb-6">
              <img
                src="/rdlogo.svg"
                alt="Logo"
                className="w-8 h-8"
                referrerPolicy="no-referrer"
              />
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white leading-tight mb-4 tracking-tighter">
              {t.heroTitle}
            </h2>
            <p className="text-slate-400 font-medium text-base mb-8 leading-relaxed">
              Generate visual concepts and manufacturing-ready PRDs
            </p>

            <ul className="space-y-5">
              {[
                { text: t.benefit1, icon: <Cpu className="w-4 h-4" /> },
                { text: t.benefit2, icon: <Palette className="w-4 h-4" /> },
                { text: t.benefit3, icon: <Check className="w-4 h-4" /> },
                { text: t.benefit4, icon: <Rocket className="w-4 h-4" /> },
              ].map((benefit, i) => (
                <li
                  key={i}
                  className="flex items-center gap-4 text-white/90 font-bold group/item"
                >
                  <div className="w-8 h-8 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center shrink-0 group-hover/item:bg-red-600 group-hover/item:border-red-600 transition-all duration-300">
                    <span className="text-white/60 group-hover/item:text-white">
                      {benefit.icon}
                    </span>
                  </div>
                  <span className="text-xs font-bold tracking-normal">
                    {benefit.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative z-10 pt-8 border-t border-white/10 mt-8">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="w-8 h-8 rounded-full border-2 border-slate-950 bg-slate-800 overflow-hidden"
                  >
                    <img
                      src={`https://picsum.photos/seed/${i + 10}/100/100`}
                      alt="User"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                ))}
              </div>
              <span className="text-[10px] font-black text-slate-500 tracking-wider">
                Trusted by 2,000+ Innovators
              </span>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="md:w-7/12 p-8 md:p-12 flex flex-col justify-center bg-white">
          <div className="max-w-md mx-auto w-full">
            <div className="mb-6">
              <h3 className="text-3xl font-black text-black mb-2 tracking-tight">
                {isLogin ? t.login : t.createAccount}
              </h3>
              <p className="text-slate-500 font-medium text-sm">
                {isLogin
                  ? "Welcome back to the laboratory."
                  : "Step into a smarter design workflow"}
              </p>
              {!isLogin && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-red-50 text-red-600 rounded-full border border-red-100 animate-pulse">
                  <Sparkles className="w-3 h-3 fill-current" />
                  <span className="text-[10px] font-black tracking-normal">
                    {t.regBonus}
                  </span>
                </div>
              )}
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-600 text-sm font-bold animate-shake">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 tracking-wider flex items-center gap-2">
                  {t.email} <span className="text-red-500">*</span>
                </label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-red-500 transition-colors" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-100 rounded-xl text-black font-bold outline-none focus:border-red-600 focus:bg-white focus:ring-4 focus:ring-red-600/5 transition-all text-sm"
                  />
                </div>
              </div>

              {!isLogin && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 tracking-wider">
                      {t.firstName} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First name"
                      className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-xl text-black font-bold outline-none focus:border-red-600 focus:bg-white focus:ring-4 focus:ring-red-600/5 transition-all text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 tracking-wider">
                      {t.lastName} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last name"
                      className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-xl text-black font-bold outline-none focus:border-red-600 focus:bg-white focus:ring-4 focus:ring-red-600/5 transition-all text-sm"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 tracking-wider flex items-center gap-2">
                  {t.password} <span className="text-red-500">*</span>
                </label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-red-500 transition-colors" />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full pl-12 pr-12 py-3.5 bg-slate-50 border border-slate-100 rounded-xl text-black font-bold outline-none focus:border-red-600 focus:bg-white focus:ring-4 focus:ring-red-600/5 transition-all text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 hover:scale-110 active:scale-90 transition-all"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {!isLogin && (
                  <div className="flex flex-wrap gap-x-4 gap-y-2 mt-2">
                    {[
                      {
                        label: t.passwordHint1,
                        met: password.length > 0 && /[a-zA-Z]/.test(password),
                      },
                      {
                        label: t.passwordHint2,
                        met: password.length > 0 && /[0-9]/.test(password),
                      },
                      { label: t.passwordHint3, met: password.length >= 8 },
                    ].map((hint, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <div
                          className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center transition-colors ${hint.met ? "bg-emerald-500 border-emerald-500" : "border-slate-200"}`}
                        >
                          {hint.met && <Check className="w-2 h-2 text-white" />}
                        </div>
                        <span
                          className={`text-[9px] font-black tracking-wider ${hint.met ? "text-emerald-600" : "text-slate-400"}`}
                        >
                          {hint.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {!isLogin && (
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-slate-200 text-red-600 focus:ring-red-600 transition-all cursor-pointer"
                  />
                  <label
                    htmlFor="terms"
                    className="text-[10px] font-bold text-slate-500 leading-relaxed cursor-pointer"
                  >
                    {t.agree}{" "}
                    <span className="text-red-600 underline hover:text-red-700">
                      {t.terms}
                    </span>{" "}
                    {t.and}{" "}
                    <span className="text-red-600 underline hover:text-red-700">
                      {t.privacy}
                    </span>
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-red-600 text-white rounded-lg text-[14px] font-black tracking-wider hover:bg-red-700 hover:scale-[1.02] active:scale-95 transition-all shadow-xl shadow-red-600/20 disabled:opacity-50 disabled:pointer-events-none"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto"></div>
                ) : isLogin ? (
                  t.signIn
                ) : (
                  t.signUp
                )}
              </button>

              <div className="flex justify-center pt-2">
                <div className="inline-flex items-center gap-3 px-4 py-2 bg-slate-50 rounded-xl border border-slate-100 transition-all hover:bg-slate-100/50">
                  <p className="text-[10px] font-bold text-slate-400 tracking-wider whitespace-nowrap">
                    {isLogin ? t.dontHave : t.alreadyHave}
                  </p>
                  <div className="w-px h-3 bg-slate-200" />
                  <button
                    type="button"
                    onClick={() => setIsLogin(!isLogin)}
                    className="text-red-600 font-black text-[11px] tracking-wider hover:text-red-700 hover:scale-[1.05] active:scale-95 transition-all whitespace-nowrap"
                  >
                    {isLogin ? t.signUp : t.login}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
